import { CdkStepLabel, CdkStepperNext, CdkStepperPrevious, CdkStepHeader, CdkStep, CdkStepper } from '@angular/cdk/stepper';
import * as i0 from '@angular/core';
import { Directive, Injectable, Optional, SkipSelf, HostBinding, Input, ChangeDetectionStrategy, ViewEncapsulation, Component, ContentChild, EventEmitter, ContentChildren, ViewChildren, Output, NgModule } from '@angular/core';
import { trigger, state, transition, style, animate } from '@angular/animations';
import { Subject, Subscription } from 'rxjs';
import { NgTemplateOutlet, CommonModule } from '@angular/common';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import * as i2 from '@angular/cdk/a11y';
import { MatIconModule } from '@angular/material/icon';
import { ErrorStateMatcher } from '@angular/material/core';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import * as i1 from '@angular/cdk/bidi';

/**
 * To use this component in a module, you can import the `CentricStepperModule`:
 * @example
 * ```typescript
 * import { CentricStepperModule } from '@windmill/ng-windmill/stepper';
 * ```
 *
 * To use the standalone component directly, you can import `CentricStepLabelDirective`:
 * @example
 * ```typescript
 * import { CentricStepLabelDirective } from '@windmill/ng-windmill/stepper';
 * ```
 */
class CentricStepLabelDirective extends CdkStepLabel {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepLabelDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: CentricStepLabelDirective, isStandalone: true, selector: "[centricStepLabel]", usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[centricStepLabel]',
                    standalone: true
                }]
        }] });

const centricStepperAnimations = {
    horizontalStepTransition: trigger('stepTransition', [
        state('previous', style({ transform: 'translate3d(-100%, 0, 0)', visibility: 'hidden' })),
        state('current', style({ transform: 'none', visibility: 'visible' })),
        state('next', style({ transform: 'translate3d(100%, 0, 0)', visibility: 'hidden' })),
        transition('* => *', animate('500ms cubic-bezier(0.35, 0, 0.25, 1)'))
    ]),
    verticalStepTransition: trigger('stepTransition', [
        state('previous', style({ height: '0px', visibility: 'hidden', opacity: 0, transform: 'scale(1, 0)' })),
        state('next', style({ height: '0px', visibility: 'hidden', opacity: 0, transform: 'scale(1, 0)' })),
        state('current', style({ height: '*', visibility: 'visible', opacity: 1, transform: 'scale(1, 1)' })),
        transition('* <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
};

class CentricStepperIntl {
    changes = new Subject();
    optionalLabel = 'Optional';
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperIntl, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperIntl, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperIntl, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
function CENTRIC_STEPPER_INTL_PROVIDER_FACTORY(parentIntl) {
    return parentIntl || new CentricStepperIntl();
}
const CENTRIC_STEPPER_INTL_PROVIDER = {
    provide: CentricStepperIntl,
    deps: [[new Optional(), new SkipSelf(), CentricStepperIntl]],
    useFactory: CENTRIC_STEPPER_INTL_PROVIDER_FACTORY
};

/**
 * To use this component in a module, you can import the `CentricStepperModule`:
 * @example
 * ```typescript
 * import { CentricStepperModule } from '@windmill/ng-windmill/stepper';
 * ```
 *
 * To use the standalone component directly, you can import `CentricStepperNextDirective`:
 * @example
 * ```typescript
 * import { CentricStepperNextDirective } from '@windmill/ng-windmill/stepper';
 * ```
 */
class CentricStepperNextDirective extends CdkStepperNext {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperNextDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: CentricStepperNextDirective, isStandalone: true, selector: "centric-button[centricStepperNext]", usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperNextDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'centric-button[centricStepperNext]',
                    standalone: true
                }]
        }] });

/**
 * To use this component in a module, you can import the `CentricStepperModule`:
 * @example
 * ```typescript
 * import { CentricStepperModule } from '@windmill/ng-windmill/stepper';
 * ```
 *
 * To use the standalone component directly, you can import `CentricStepperPreviousDirective`:
 * @example
 * ```typescript
 * import { CentricStepperPreviousDirective } from '@windmill/ng-windmill/stepper';
 * ```
 */
class CentricStepperPreviousDirective extends CdkStepperPrevious {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperPreviousDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: CentricStepperPreviousDirective, isStandalone: true, selector: "centric-button[centricStepperPrevious]", usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperPreviousDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'centric-button[centricStepperPrevious]',
                    standalone: true
                }]
        }] });

/**
 * To use this component in a module, you can import the `CentricStepperModule`:
 * @example
 * ```typescript
 * import { CentricStepperModule } from '@windmill/ng-windmill/stepper';
 * ```
 *
 * To use the standalone component directly, you can import `CentricStepHeaderComponent`:
 * @example
 * ```typescript
 * import { CentricStepHeaderComponent } from '@windmill/ng-windmill/stepper';
 * ```
 */
class CentricStepHeaderComponent extends CdkStepHeader {
    intl;
    focusMonitor;
    /**
     * State of the given step
     */
    state;
    /**
     * Label of the given step
     */
    label;
    /**
     * Error message to display when there's an error
     */
    errorMessage;
    /**
     * Index of the given step
     */
    index;
    /**
     * The given step is selected
     */
    selected = false;
    /**
     * The given step is active
     */
    active = false;
    /**
     * The given step is optional
     */
    optional = false;
    /**
     * The custom icon for step
     */
    svgIcon;
    containerClass = true;
    roleStepperHorizontal = 'tab';
    intlSubscription;
    constructor(intl, focusMonitor, elementRef, changeDetectorRef) {
        super(elementRef);
        this.intl = intl;
        this.focusMonitor = focusMonitor;
        focusMonitor.monitor(elementRef, true);
        this.intlSubscription = intl.changes.subscribe(() => changeDetectorRef.markForCheck());
    }
    ngOnDestroy() {
        this.intlSubscription.unsubscribe();
        this.focusMonitor.stopMonitoring(this._elementRef);
    }
    focus(origin, options) {
        if (origin) {
            this.focusMonitor.focusVia(this._elementRef, origin, options);
        }
        else {
            this._elementRef.nativeElement.focus(options);
        }
    }
    stringLabel() {
        return this.label instanceof CentricStepLabelDirective ? null : this.label;
    }
    templateLabel() {
        return this.label instanceof CentricStepLabelDirective ? this.label : null;
    }
    getHostElement() {
        return this._elementRef.nativeElement;
    }
    getDefaultTextForState() {
        return `${this.index + 1}`;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepHeaderComponent, deps: [{ token: CentricStepperIntl }, { token: i2.FocusMonitor }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: CentricStepHeaderComponent, isStandalone: true, selector: "centric-step-header", inputs: { state: "state", label: "label", errorMessage: "errorMessage", index: "index", selected: "selected", active: "active", optional: "optional", svgIcon: "svgIcon" }, host: { properties: { "class.centric-step-header": "this.containerClass", "attr.role": "this.roleStepperHorizontal" } }, usesInheritance: true, ngImport: i0, template: "<div\r\n  class=\"centric-step-icon-state-{{state}} centric-step-icon\"\r\n  [class.centric-step-icon-selected]=\"selected\">\r\n  <div class=\"centric-step-icon-content\">\r\n    @if (svgIcon) {\r\n    <centric-icon2\r\n      [svgIcon]=\"svgIcon\"\r\n      [size]=\"'20'\"></centric-icon2>\r\n    } @else {\r\n    <span>{{ getDefaultTextForState() }}</span>\r\n    }\r\n  </div>\r\n</div>\r\n<div\r\n  class=\"centric-step-label\"\r\n  [class.centric-step-label-active]=\"active\"\r\n  [class.centric-step-label-selected]=\"selected\"\r\n  [class.centric-step-label-error]=\"state == 'error'\">\r\n  @if (templateLabel()) {\r\n  <ng-container [ngTemplateOutlet]=\"templateLabel()!.template\"> </ng-container>\r\n  } @if (stringLabel()) {\r\n  <div class=\"centric-step-text-label\">{{label}}</div>\r\n  } @if (optional && state != 'error') {\r\n  <div class=\"centric-step-optional\">{{ intl.optionalLabel }}</div>\r\n  } @if (state == 'error') {\r\n  <div class=\"centric-step-sub-label-error\">{{ errorMessage }}</div>\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-step-header', encapsulation: ViewEncapsulation.None, imports: [NgTemplateOutlet, CentricIcon2Component], changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<div\r\n  class=\"centric-step-icon-state-{{state}} centric-step-icon\"\r\n  [class.centric-step-icon-selected]=\"selected\">\r\n  <div class=\"centric-step-icon-content\">\r\n    @if (svgIcon) {\r\n    <centric-icon2\r\n      [svgIcon]=\"svgIcon\"\r\n      [size]=\"'20'\"></centric-icon2>\r\n    } @else {\r\n    <span>{{ getDefaultTextForState() }}</span>\r\n    }\r\n  </div>\r\n</div>\r\n<div\r\n  class=\"centric-step-label\"\r\n  [class.centric-step-label-active]=\"active\"\r\n  [class.centric-step-label-selected]=\"selected\"\r\n  [class.centric-step-label-error]=\"state == 'error'\">\r\n  @if (templateLabel()) {\r\n  <ng-container [ngTemplateOutlet]=\"templateLabel()!.template\"> </ng-container>\r\n  } @if (stringLabel()) {\r\n  <div class=\"centric-step-text-label\">{{label}}</div>\r\n  } @if (optional && state != 'error') {\r\n  <div class=\"centric-step-optional\">{{ intl.optionalLabel }}</div>\r\n  } @if (state == 'error') {\r\n  <div class=\"centric-step-sub-label-error\">{{ errorMessage }}</div>\r\n  }\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: CentricStepperIntl }, { type: i2.FocusMonitor }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }], propDecorators: { state: [{
                type: Input
            }], label: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], index: [{
                type: Input
            }], selected: [{
                type: Input
            }], active: [{
                type: Input
            }], optional: [{
                type: Input
            }], svgIcon: [{
                type: Input
            }], containerClass: [{
                type: HostBinding,
                args: ['class.centric-step-header']
            }], roleStepperHorizontal: [{
                type: HostBinding,
                args: ['attr.role']
            }] } });

/**
 * To use this component in a module, you can import the `CentricStepperModule`:
 * @example
 * ```typescript
 * import { CentricStepperModule } from '@windmill/ng-windmill/stepper';
 * ```
 *
 * To use the standalone component directly, you can import `CentricStepComponent`:
 * @example
 * ```typescript
 * import { CentricStepComponent } from '@windmill/ng-windmill/stepper';
 * ```
 */
class CentricStepComponent extends CdkStep {
    /**
     * The custom icon for step
     */
    svgIcon;
    /**
     * Data-testid attribute for component
     */
    testStepId;
    stepLabel;
    isErrorState(control, form) {
        const originalErrorState = Boolean(control?.invalid && (control.touched || form?.submitted));
        const customErrorState = Boolean(control?.invalid && this.interacted);
        return originalErrorState || customErrorState;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: CentricStepComponent, isStandalone: true, selector: "centric-step", inputs: { svgIcon: "svgIcon", testStepId: "testStepId" }, providers: [{ provide: ErrorStateMatcher, useExisting: CentricStepComponent }], queries: [{ propertyName: "stepLabel", first: true, predicate: CentricStepLabelDirective, descendants: true }], exportAs: ["matStep"], usesInheritance: true, ngImport: i0, template: "<ng-template>\r\n  <ng-content></ng-content>\r\n</ng-template>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-step', exportAs: 'matStep', encapsulation: ViewEncapsulation.None, providers: [{ provide: ErrorStateMatcher, useExisting: CentricStepComponent }], changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<ng-template>\r\n  <ng-content></ng-content>\r\n</ng-template>\r\n" }]
        }], propDecorators: { svgIcon: [{
                type: Input
            }], testStepId: [{
                type: Input
            }], stepLabel: [{
                type: ContentChild,
                args: [CentricStepLabelDirective]
            }] } });

/**
 * To use this component in a module, you can import the `CentricStepperModule`:
 * @example
 * ```typescript
 * import { CentricStepperModule } from '@windmill/ng-windmill/stepper';
 * ```
 *
 * To use the standalone component directly, you can import `CentricStepperDirective`:
 * @example
 * ```typescript
 * import { CentricStepperDirective } from '@windmill/ng-windmill/stepper';
 * ```
 */
class CentricStepperDirective extends CdkStepper {
    /**
     * Disable inactive steps
     */
    disableInactiveSteps = false;
    /**
     * Event emitted when the current step is done transitioning in
     */
    animationDone = new EventEmitter();
    _stepHeader;
    steps;
    stepsChangeSubscription = Subscription.EMPTY;
    animationEvent = new Subject();
    ngAfterContentInit() {
        this.stepsChangeSubscription = this.steps.changes.pipe(takeUntil(this._destroyed)).subscribe(() => this._stateChanged());
        this.animationEvent
            .pipe(distinctUntilChanged((x, y) => x.fromState === y.fromState && x.toState === y.toState), takeUntil(this._destroyed))
            .subscribe(event => {
            if (event.toState === 'current') {
                this.animationDone.emit();
            }
        });
    }
    ngOnDestroy() {
        this.stepsChangeSubscription.unsubscribe();
    }
    getTabIndex(i) {
        return this._getFocusIndex() === i ? '0' : '-1';
    }
    isStepActive(step, index) {
        return (step.completed || this.selectedIndex === index || !this.linear) && !this.isStepDisabled(step, index);
    }
    isStepDisabled(step, index) {
        if (!this.linear) {
            return false;
        }
        const isDisabled = this.selectedIndex !== index && this.disableInactiveSteps;
        return !step.completed && isDisabled;
    }
    onKeydown(event, step, index) {
        if (this.isStepDisabled(step, index) && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            return;
        }
        this._onKeydown(event);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: CentricStepperDirective, isStandalone: true, selector: "[centricStepper]", inputs: { disableInactiveSteps: "disableInactiveSteps" }, outputs: { animationDone: "animationDone" }, providers: [{ provide: CdkStepper, useExisting: CentricStepperDirective }], queries: [{ propertyName: "steps", predicate: CentricStepComponent }], viewQueries: [{ propertyName: "_stepHeader", predicate: CentricStepHeaderComponent, descendants: true }], usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[centricStepper]',
                    providers: [{ provide: CdkStepper, useExisting: CentricStepperDirective }],
                    standalone: true
                }]
        }], propDecorators: { disableInactiveSteps: [{
                type: Input
            }], animationDone: [{
                type: Output
            }], _stepHeader: [{
                type: ViewChildren,
                args: [CentricStepHeaderComponent]
            }], steps: [{
                type: ContentChildren,
                args: [CentricStepComponent]
            }] } });

/**
 * To use this component in a module, you can import the `CentricStepperModule`:
 * @example
 * ```typescript
 * import { CentricStepperModule } from '@windmill/ng-windmill/stepper';
 * ```
 *
 * To use the standalone component directly, you can import `CentricHorizontalStepperComponent`:
 * @example
 * ```typescript
 * import { CentricHorizontalStepperComponent } from '@windmill/ng-windmill/stepper';
 * ```
 */
class CentricHorizontalStepperComponent extends CentricStepperDirective {
    containerClass = true;
    roleStepper = 'tablist';
    ariaOrientationStepperHorizontal = 'horizontal';
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricHorizontalStepperComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: CentricHorizontalStepperComponent, isStandalone: true, selector: "centric-horizontal-stepper", host: { properties: { "class.centric-stepper-horizontal": "this.containerClass", "attr.role": "this.roleStepper", "attr.aria-orientation": "this.ariaOrientationStepperHorizontal" } }, providers: [
            { provide: CentricStepperDirective, useExisting: CentricHorizontalStepperComponent },
            { provide: CdkStepper, useExisting: CentricHorizontalStepperComponent }
        ], exportAs: ["centricHorizontalStepper"], usesInheritance: true, ngImport: i0, template: "<div class=\"centric-horizontal-stepper-header-container\">\r\n  @for (step of steps; track step; let i = $index; let isLast = $last) {\r\n  <centric-step-header\r\n    class=\"centric-horizontal-stepper-header {{_getIndicatorType(i, step.state)}}\"\r\n    [class.is-last]=\"isLast\"\r\n    [class.is-first]=\"i === 0\"\r\n    [class.is-disabled]=\"isStepDisabled(step, i)\"\r\n    [class.is-active]=\"step.completed\"\r\n    [class.has-label]=\"step.stepLabel || step.label\"\r\n    [class.non-editable]=\"!step.editable\"\r\n    [attr.tabIndex]=\"getTabIndex(i)\"\r\n    [attr.data-testid]=\"step.testStepId\"\r\n    [attr.aria-posinset]=\"i + 1\"\r\n    [attr.aria-setsize]=\"steps.length\"\r\n    [attr.aria-controls]=\"_getStepContentId(i)\"\r\n    [attr.aria-selected]=\"selectedIndex == i\"\r\n    [attr.aria-label]=\"step.ariaLabel || null\"\r\n    [attr.aria-labelledby]=\"(!step.ariaLabel && step.ariaLabelledby) ? step.ariaLabelledby : null\"\r\n    [index]=\"i\"\r\n    [state]=\"_getIndicatorType(i, step.state)\"\r\n    [label]=\"step.stepLabel || step.label\"\r\n    [selected]=\"selectedIndex === i\"\r\n    [active]=\"isStepActive(step,i)\"\r\n    [optional]=\"step.optional\"\r\n    [errorMessage]=\"step.errorMessage\"\r\n    [svgIcon]=\"step.svgIcon\"\r\n    (click)=\"step.select()\"\r\n    (keydown)=\"onKeydown($event, step, i)\">\r\n  </centric-step-header>\r\n  }\r\n</div>\r\n\r\n<div class=\"centric-horizontal-content-container\">\r\n  @for (step of steps; track step; let i = $index) {\r\n  <div\r\n    class=\"centric-horizontal-stepper-content\"\r\n    role=\"tabpanel\"\r\n    [@stepTransition]=\"_getAnimationDirection(i)\"\r\n    (@stepTransition.done)=\"animationEvent.next($event)\"\r\n    [id]=\"_getStepContentId(i)\"\r\n    [attr.aria-labelledby]=\"_getStepLabelId(i)\"\r\n    [attr.aria-expanded]=\"selectedIndex === i\"\r\n    [class.is-disabled]=\"isStepDisabled(step, i)\">\r\n    <ng-container [ngTemplateOutlet]=\"step.content\"></ng-container>\r\n  </div>\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "component", type: CentricStepHeaderComponent, selector: "centric-step-header", inputs: ["state", "label", "errorMessage", "index", "selected", "active", "optional", "svgIcon"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], animations: [centricStepperAnimations.horizontalStepTransition], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricHorizontalStepperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-horizontal-stepper', exportAs: 'centricHorizontalStepper', encapsulation: ViewEncapsulation.None, animations: [centricStepperAnimations.horizontalStepTransition], imports: [CentricStepHeaderComponent, NgTemplateOutlet], providers: [
                        { provide: CentricStepperDirective, useExisting: CentricHorizontalStepperComponent },
                        { provide: CdkStepper, useExisting: CentricHorizontalStepperComponent }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<div class=\"centric-horizontal-stepper-header-container\">\r\n  @for (step of steps; track step; let i = $index; let isLast = $last) {\r\n  <centric-step-header\r\n    class=\"centric-horizontal-stepper-header {{_getIndicatorType(i, step.state)}}\"\r\n    [class.is-last]=\"isLast\"\r\n    [class.is-first]=\"i === 0\"\r\n    [class.is-disabled]=\"isStepDisabled(step, i)\"\r\n    [class.is-active]=\"step.completed\"\r\n    [class.has-label]=\"step.stepLabel || step.label\"\r\n    [class.non-editable]=\"!step.editable\"\r\n    [attr.tabIndex]=\"getTabIndex(i)\"\r\n    [attr.data-testid]=\"step.testStepId\"\r\n    [attr.aria-posinset]=\"i + 1\"\r\n    [attr.aria-setsize]=\"steps.length\"\r\n    [attr.aria-controls]=\"_getStepContentId(i)\"\r\n    [attr.aria-selected]=\"selectedIndex == i\"\r\n    [attr.aria-label]=\"step.ariaLabel || null\"\r\n    [attr.aria-labelledby]=\"(!step.ariaLabel && step.ariaLabelledby) ? step.ariaLabelledby : null\"\r\n    [index]=\"i\"\r\n    [state]=\"_getIndicatorType(i, step.state)\"\r\n    [label]=\"step.stepLabel || step.label\"\r\n    [selected]=\"selectedIndex === i\"\r\n    [active]=\"isStepActive(step,i)\"\r\n    [optional]=\"step.optional\"\r\n    [errorMessage]=\"step.errorMessage\"\r\n    [svgIcon]=\"step.svgIcon\"\r\n    (click)=\"step.select()\"\r\n    (keydown)=\"onKeydown($event, step, i)\">\r\n  </centric-step-header>\r\n  }\r\n</div>\r\n\r\n<div class=\"centric-horizontal-content-container\">\r\n  @for (step of steps; track step; let i = $index) {\r\n  <div\r\n    class=\"centric-horizontal-stepper-content\"\r\n    role=\"tabpanel\"\r\n    [@stepTransition]=\"_getAnimationDirection(i)\"\r\n    (@stepTransition.done)=\"animationEvent.next($event)\"\r\n    [id]=\"_getStepContentId(i)\"\r\n    [attr.aria-labelledby]=\"_getStepLabelId(i)\"\r\n    [attr.aria-expanded]=\"selectedIndex === i\"\r\n    [class.is-disabled]=\"isStepDisabled(step, i)\">\r\n    <ng-container [ngTemplateOutlet]=\"step.content\"></ng-container>\r\n  </div>\r\n  }\r\n</div>\r\n" }]
        }], propDecorators: { containerClass: [{
                type: HostBinding,
                args: ['class.centric-stepper-horizontal']
            }], roleStepper: [{
                type: HostBinding,
                args: ['attr.role']
            }], ariaOrientationStepperHorizontal: [{
                type: HostBinding,
                args: ['attr.aria-orientation']
            }] } });

/**
 * To use this component in a module, you can import the `CentricStepperModule`:
 * @example
 * ```typescript
 * import { CentricStepperModule } from '@windmill/ng-windmill/stepper';
 * ```
 *
 * To use the standalone component directly, you can import `CentricVerticalStepperComponent`:
 * @example
 * ```typescript
 * import { CentricVerticalStepperComponent } from '@windmill/ng-windmill/stepper';
 * ```
 */
class CentricVerticalStepperComponent extends CentricStepperDirective {
    containerClass = true;
    roleStepper = 'tablist';
    ariaOrientationStepperHorizontal = 'vertical';
    constructor(dir, changeDetectorRef, elementRef) {
        super(dir, changeDetectorRef, elementRef);
        this.orientation = 'vertical';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricVerticalStepperComponent, deps: [{ token: i1.Directionality, optional: true }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: CentricVerticalStepperComponent, isStandalone: true, selector: "centric-vertical-stepper", host: { properties: { "class.centric-stepper-vertical": "this.containerClass", "attr.role": "this.roleStepper", "attr.aria-orientation": "this.ariaOrientationStepperHorizontal" } }, providers: [
            { provide: CentricStepperDirective, useExisting: CentricVerticalStepperComponent },
            { provide: CdkStepper, useExisting: CentricVerticalStepperComponent }
        ], exportAs: ["centricVerticalStepper"], usesInheritance: true, ngImport: i0, template: "@for (step of steps; track step; let i = $index; let isLast = $last) {\r\n<div\r\n  class=\"centric-step\"\r\n  [class.is-active]=\"step.completed\"\r\n  [class.is-disabled]=\"isStepDisabled(step, i)\">\r\n  <centric-step-header\r\n    class=\"centric-vertical-stepper-header\"\r\n    [class.is-disabled]=\"isStepDisabled(step, i)\"\r\n    [class.is-first]=\"i == 0\"\r\n    [class.is-last]=\"isLast\"\r\n    [attr.tabIndex]=\"getTabIndex(i)\"\r\n    [attr.data-testid]=\"step.testStepId\"\r\n    [attr.aria-posinset]=\"i + 1\"\r\n    [attr.aria-setsize]=\"steps.length\"\r\n    [attr.aria-controls]=\"_getStepContentId(i)\"\r\n    [attr.aria-selected]=\"selectedIndex === i\"\r\n    [attr.aria-label]=\"step.ariaLabel || null\"\r\n    [attr.aria-labelledby]=\"(!step.ariaLabel && step.ariaLabelledby) ? step.ariaLabelledby : null\"\r\n    [index]=\"i\"\r\n    [state]=\"_getIndicatorType(i, step.state)\"\r\n    [label]=\"step.stepLabel || step.label\"\r\n    [selected]=\"selectedIndex === i\"\r\n    [active]=\"isStepActive(step,i)\"\r\n    [optional]=\"step.optional\"\r\n    [errorMessage]=\"step.errorMessage\"\r\n    [svgIcon]=\"step.svgIcon\"\r\n    (click)=\"step.select()\"\r\n    (keydown)=\"onKeydown($event, step, i)\">\r\n  </centric-step-header>\r\n  <div\r\n    class=\"centric-vertical-content-container\"\r\n    [class.centric-stepper-vertical-line]=\"!isLast\"\r\n    [class.is-active]=\"step.completed\">\r\n    <div\r\n      class=\"centric-vertical-stepper-content\"\r\n      role=\"tabpanel\"\r\n      [@stepTransition]=\"_getAnimationDirection(i)\"\r\n      (@stepTransition.done)=\"animationEvent.next($event)\"\r\n      [id]=\"_getStepContentId(i)\"\r\n      [attr.aria-labelledby]=\"_getStepLabelId(i)\"\r\n      [attr.aria-expanded]=\"selectedIndex === i\">\r\n      <div class=\"centric-vertical-content\">\r\n        <ng-container [ngTemplateOutlet]=\"step.content\"></ng-container>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n}\r\n", dependencies: [{ kind: "component", type: CentricStepHeaderComponent, selector: "centric-step-header", inputs: ["state", "label", "errorMessage", "index", "selected", "active", "optional", "svgIcon"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], animations: [centricStepperAnimations.verticalStepTransition], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricVerticalStepperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-vertical-stepper', exportAs: 'centricVerticalStepper', encapsulation: ViewEncapsulation.None, animations: [centricStepperAnimations.verticalStepTransition], imports: [CentricStepHeaderComponent, NgTemplateOutlet], providers: [
                        { provide: CentricStepperDirective, useExisting: CentricVerticalStepperComponent },
                        { provide: CdkStepper, useExisting: CentricVerticalStepperComponent }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "@for (step of steps; track step; let i = $index; let isLast = $last) {\r\n<div\r\n  class=\"centric-step\"\r\n  [class.is-active]=\"step.completed\"\r\n  [class.is-disabled]=\"isStepDisabled(step, i)\">\r\n  <centric-step-header\r\n    class=\"centric-vertical-stepper-header\"\r\n    [class.is-disabled]=\"isStepDisabled(step, i)\"\r\n    [class.is-first]=\"i == 0\"\r\n    [class.is-last]=\"isLast\"\r\n    [attr.tabIndex]=\"getTabIndex(i)\"\r\n    [attr.data-testid]=\"step.testStepId\"\r\n    [attr.aria-posinset]=\"i + 1\"\r\n    [attr.aria-setsize]=\"steps.length\"\r\n    [attr.aria-controls]=\"_getStepContentId(i)\"\r\n    [attr.aria-selected]=\"selectedIndex === i\"\r\n    [attr.aria-label]=\"step.ariaLabel || null\"\r\n    [attr.aria-labelledby]=\"(!step.ariaLabel && step.ariaLabelledby) ? step.ariaLabelledby : null\"\r\n    [index]=\"i\"\r\n    [state]=\"_getIndicatorType(i, step.state)\"\r\n    [label]=\"step.stepLabel || step.label\"\r\n    [selected]=\"selectedIndex === i\"\r\n    [active]=\"isStepActive(step,i)\"\r\n    [optional]=\"step.optional\"\r\n    [errorMessage]=\"step.errorMessage\"\r\n    [svgIcon]=\"step.svgIcon\"\r\n    (click)=\"step.select()\"\r\n    (keydown)=\"onKeydown($event, step, i)\">\r\n  </centric-step-header>\r\n  <div\r\n    class=\"centric-vertical-content-container\"\r\n    [class.centric-stepper-vertical-line]=\"!isLast\"\r\n    [class.is-active]=\"step.completed\">\r\n    <div\r\n      class=\"centric-vertical-stepper-content\"\r\n      role=\"tabpanel\"\r\n      [@stepTransition]=\"_getAnimationDirection(i)\"\r\n      (@stepTransition.done)=\"animationEvent.next($event)\"\r\n      [id]=\"_getStepContentId(i)\"\r\n      [attr.aria-labelledby]=\"_getStepLabelId(i)\"\r\n      [attr.aria-expanded]=\"selectedIndex === i\">\r\n      <div class=\"centric-vertical-content\">\r\n        <ng-container [ngTemplateOutlet]=\"step.content\"></ng-container>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n}\r\n" }]
        }], ctorParameters: () => [{ type: i1.Directionality, decorators: [{
                    type: Optional
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }], propDecorators: { containerClass: [{
                type: HostBinding,
                args: ['class.centric-stepper-vertical']
            }], roleStepper: [{
                type: HostBinding,
                args: ['attr.role']
            }], ariaOrientationStepperHorizontal: [{
                type: HostBinding,
                args: ['attr.aria-orientation']
            }] } });

const moduleComponents = [
    CentricHorizontalStepperComponent,
    CentricStepComponent,
    CentricStepHeaderComponent,
    CentricStepLabelDirective,
    CentricStepperDirective,
    CentricStepperNextDirective,
    CentricStepperPreviousDirective,
    CentricVerticalStepperComponent
];
class CentricStepperModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperModule, imports: [CommonModule, MatIconModule, CentricIcon2Module, CentricHorizontalStepperComponent,
            CentricStepComponent,
            CentricStepHeaderComponent,
            CentricStepLabelDirective,
            CentricStepperDirective,
            CentricStepperNextDirective,
            CentricStepperPreviousDirective,
            CentricVerticalStepperComponent], exports: [CentricHorizontalStepperComponent,
            CentricStepComponent,
            CentricStepHeaderComponent,
            CentricStepLabelDirective,
            CentricStepperDirective,
            CentricStepperNextDirective,
            CentricStepperPreviousDirective,
            CentricVerticalStepperComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperModule, imports: [CommonModule, MatIconModule, CentricIcon2Module, CentricHorizontalStepperComponent,
            CentricStepHeaderComponent,
            CentricVerticalStepperComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricStepperModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MatIconModule, CentricIcon2Module, ...moduleComponents],
                    exports: [...moduleComponents]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CENTRIC_STEPPER_INTL_PROVIDER, CENTRIC_STEPPER_INTL_PROVIDER_FACTORY, CentricHorizontalStepperComponent, CentricStepComponent, CentricStepHeaderComponent, CentricStepLabelDirective, CentricStepperDirective, CentricStepperIntl, CentricStepperModule, CentricStepperNextDirective, CentricStepperPreviousDirective, CentricVerticalStepperComponent, centricStepperAnimations };
